import React, { Component, createRef, Fragment } from 'react';
import { render } from 'react-dom';
import { object, arrayOf, objectOf, string, number, oneOfType, array, exact } from 'prop-types';
import { bisector } from 'd3-array';
import { scaleTime, scaleLinear } from 'd3-scale';
import { axisRight, axisBottom } from 'd3-axis';
import { timeYear, timeMonth } from 'd3-time';
import { timeFormat } from 'd3-time-format';
import { line, area } from 'd3-shape';
import { select, selectAll } from 'd3-selection';
import Axis from 'components/chart-utils/axis';
import OverlayModal from 'components/overlay-modal/component';
import LineThickness from '../chart-component-line-thickness/linethickness';
import RangeSlider from '../chart-component-rangeslider/rangeslider';
import { getTextWidth } from '../../utils/svg/text-utils';
import { domToPng } from 'modern-screenshot';

export default class Line extends Component {

    static propTypes = {
        data: arrayOf( oneOfType( [ string, number, array ] ) ).isRequired,
        chart: objectOf( oneOfType( [ string, array ] ) ).isRequired,
        height: number.isRequired,
        width: number.isRequired,
        margin: exact( { top: number, right: number, bottom: number, left: number } ),
        svgRef: object.isRequired,
        disclaimer: object,
        pageWrapper: object.isRequired,
        componentWrapper: object.isRequired,
        overlay: objectOf( string ).isRequired
    };

    constructor( props ) {

        super( props );

        this.columnRowLabels = Array.from( this.props.data[0] );
        this.colorScale = this.initializeChartLineColors();

        this.sanitizedChartData = this.sanitizeChartData();
        this.initialChartDisplayYears = this.initializeChartDisplayYears();

        this.recessionPeriods = this.initializeRecessionPeriods();

        this.scrubberRef = createRef();
        this.lineRef = createRef();
        this.modalRef = createRef();
        this.toolTipRef = createRef();
        this.sliderRef = createRef();
        this.focusRef = createRef();

        this.svgRef = createRef();
        this.headerRef = createRef();
        this.subHeaderRef = createRef();

        this.toFixedValue = 2;
        this.state = {
            domain: this.getDomain( this.sanitizedChartData ),
            chartData: this.sanitizedChartData,
            chartDisplayYears: this.initialChartDisplayYears,
            tooltipData: this.sanitizedChartData[0],
            recessionPeriods: this.recessionPeriods,
            lineThicknessValue: 3,
            sliderRange: null,
            mouseX: 0
        };

    }


    componentDidMount() {

        const saveButton = document.querySelector( 'button.download-png' );

        saveButton.addEventListener( 'click', this.downloadChart );

    }


    downloadChart = () => {


        this.svgRef.current.style.margin = '0';

        domToPng( this.props.svgRef.current, { quality: 1 } ).then( ( dataUrl ) => {

            const link = document.createElement( 'a' );

            link.download = this.props.chart.heading;
            link.href = dataUrl;
            link.click();

        } );

        setTimeout( () => {

            this.svgRef.current.style.margin = '-64px 0 0 0';

        }, 100 );

    };

    shouldComponentUpdate( nextProps, nextState ) {

        const toolTipUpdated = !( this.state.tooltipData.column.date === nextState.tooltipData.column.date );

        const lineThicknessUpdated = !( this.state.lineThicknessValue === nextState.lineThicknessValue );

        const rangeUpdated = !( this.state.sliderRange === nextState.sliderRange );

        return ( toolTipUpdated || lineThicknessUpdated || rangeUpdated );

    }

    /**
     * Sanitizes the raw chart data suplied via JSON
     *
     * @returns {Array} - Array of sanitized data objects, example object:
     * { date: Jan 01 1981, month: "Jan", value: 68.94, index: 0 }
     */
    sanitizeChartData = () => {

        const chartData = this.props.data.slice( 1 );
        const rowLabels = this.columnRowLabels.slice( 1 );
        const months = [ 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec' ];


        return chartData.map( ( data, i ) => {

            const dateData = data[0].split( ':' );
            const currentDate = new Date( `${ dateData[0] }/${ dateData[1] }/${ dateData[2] }` );

            const chartObj = {
                column: {
                    date: currentDate,
                    name: this.columnRowLabels[0],
                    month: months[currentDate.getMonth()],
                    day: currentDate.getDate(),
                    value: data[1],
                    index: i
                },
                rows: []
            };

            chartObj.rows = data.slice( 1 ).map( ( val, j ) => {

                const rowObj = {
                    name: rowLabels[j],
                    value: val,
                    visible: true,
                    color: this.colorScale[j]
                };

                return rowObj;

            } );

            return chartObj;

        } );

    };

    /**
     * Initializes the chart display years when component is first loaded
     *
     * Initally chart displays all data
     *
     * @returns {object} - An object with the initial start and end year/month
     */
    initializeChartDisplayYears = () => {

        const length = this.sanitizedChartData.length;

        const initialDisplay = {
            start: this.sanitizedChartData[0].column,
            end: this.sanitizedChartData[length - 1].column
        };

        return initialDisplay;

    };

    /**
     * Initializes the recession periods to be displayed on chart
     * Uses pre-defined recession periods and adds any recession periods
     * added via CMS
     * @returns {array} - Array of chart colors
     */
    initializeRecessionPeriods = () => {

        // predefined recession periods
        const recessionPeriods = [];

        // add any CMS added recessionPeriods
        return ( recessionPeriods.concat( this.props.chart.recessionPeriods ) );

    };

    /**
     * Initializes the chart line colors to be displayed on chart
     * Uses pre-defined chart line colors and adds any chart line colors
     * added via CMS
     * @returns {array} - Array of recession periods
     */
    initializeChartLineColors = () => {

        // predefined chart colors
        const chartColors = [];

        // add any CMS added chart colors
        return ( chartColors.concat( this.props.chart.chartColors ) );

    };

    /**
     * Sets the minimum and maximum values of the data displayed in the chart
     * that are then used to set the domain for the y axis
     *
     * @param {Array} [currChartData] - Array of the current data being displayed in the chart
     *
     * @returns {object} - Object with the min and max values relative to the values displayed in the chart
     */
    getDomain = ( currChartData ) => {

        const domain = {
            minValue: 0,
            maxValue: 0
        };

        // get min and max value of initial chart data
        currChartData.forEach( ( data ) => {

            data.rows.forEach( ( row ) => {

                domain.minValue = row.value <= domain.minValue ? row.value : domain.minValue;
                domain.maxValue = row.value >= domain.maxValue ? row.value : domain.maxValue;

            } );

        } );

        return domain;

    };

    /**
     * Function to customize yAxis presentation
     * @param {svg}      [g]           - Specific g element that contains the axis
     * @param {Function} [yAxisFunc]   - yAxis function
     *
     */
    customYAxis = ( g, yAxisFunc ) => {

        g.call( yAxisFunc );

        g.select( '.domain' ).remove();

        g.selectAll( '.tick text' )
            .attr( 'x', -35 )
            .attr( 'dy', 3.5 )
            .attr( 'fill', '#515c70' )
            .attr( 'font-size', '12px' )
            .attr( 'font-family', 'Graphik-Medium' );

        g.selectAll( '.tick line' )
            .attr( 'stroke-dasharray', '4' )
            .attr( 'stroke', '#929fb3' );

    };

    /**
     * Function to customize xAxis presentation for the yearly axis
     * @param {svg}      [g]           - Specific g element that contains the axis
     * @param {Function} [xAxisFunc]   - xAxis function
     *
     */
    customXYearAxis = ( g, xAxisFunc ) => {

        const yearGap = this.yearGap();

        g.call( xAxisFunc );

        g.select( '.domain' )
            .attr( 'stroke', '#515c70' )
            .attr( 'stroke-width', '1.5px' );

        if ( this.state.chartData.length < 37 ) {

            g.selectAll( '.tick line' )
                .attr( 'stroke', '#515c70' )
                .attr( 'stroke-width', '1.5px' );

            g.selectAll( '.tick text' )
                .attr( 'y', 50.5 )
                .attr( 'fill', '#515c70' )
                .attr( 'font-size', '16px' )
                .attr( 'font-family', 'Graphik-Bold' )
                .attr( 'class', 'year-month' )
                .attr( 'display', ( d, i ) => {

                    return ( i % yearGap !== 0 ? 'none' : '' );

                } );


        }
        else {

            g.selectAll( '.tick line' )
                .attr( 'stroke', '#515c70' )
                .attr( 'stroke-width', '1.5px' );

            g.selectAll( '.tick text' )
                .attr( 'y', 22 )
                .attr( 'fill', '#515c70' )
                .attr( 'font-size', '12px' )
                .attr( 'font-family', 'Graphik-Medium' )
                .attr( 'class', '' )
                .attr( 'display', ( d, i ) => {

                    return ( i % yearGap !== 0 ? 'none' : '' );

                } );

            g.select( '.tick:last-of-type text' )
                .attr( 'x', -5 );

        }


    };

    yearGap = () => {

        // 10 years
        if ( this.state.chartData.length < 3650 ) {

            return 1;

        }

        // 30 years
        if ( this.state.chartData.length < 10950 ) {

            return 2;

        }

        // 50 years
        if ( this.state.chartData.length < 18250 ) {

            return 3;

        }

        return 4;

    };

    /**
     * Renders lines onto chart
     * @returns {Array} - Array of SVG line elements representing each data line
     */
    renderLines = () => {

        this.lineFunc = line()
            .defined( ( d ) => d.value !== null )
            .x( ( d ) => this.xScale( d.date ) )
            .y( ( d ) => this.yScale( d.value ) );

        const chartData = this.state.chartData;

        // array of rows that are selected to be visible
        const visibileRows = chartData[0].rows.filter( ( row ) => row.visible );

        // for each row that is supposed to be shown
        // get associated colorData, date and value for that date
        const lineData = visibileRows.map( ( row ) => {

            return (
                {
                    id: row.name,
                    color: row.color,
                    values: chartData.map( ( data ) => {

                        return (
                            {
                                date: data.column.date,
                                value: ( data.rows.filter( ( obj ) => {

                                    if ( obj.name === row.name ) {

                                        return obj;

                                    }

                                } ) )[0].value

                            } );

                    } )

                } );

        } );

        const lines = lineData.map( ( lineObj ) => {

            return (
                <path
                    className='line'
                    fill='none'
                    stroke={ lineObj.color }
                    strokeWidth={ this.state.lineThicknessValue }
                    d={ this.lineFunc( lineObj.values ) }
                    key={ lineObj.id }
                />
            );

        } );

        return lines;

    };

    /**
     * Renders moving hover dots
     * @returns {Array} - Array of SVG Cirle elements representing each hover dot
     */
    renderHoverDots = () => {

        const chartData = this.state.tooltipData;

        // array of rows displayed in the chart
        const chartRows = chartData.rows;

        const dots = chartRows.map( ( line, i ) => {

            if ( line.visible ) {

                return (
                    <circle
                        r='7'
                        opacity={ 0 }
                        key={ i }
                        strokeWidth={ 2 }
                        className='dot-per-line'
                    />
                );

            }

        } );

        return dots;

    };

    /**
     * Renders recession periods on to the chart
     * @returns {Array} - Array of <path> elements representing each recession period
     */
    renderRecessionPeriod = () => {

        const areaArr = [];

        var months = {
            jan: 0,
            feb: 1,
            mar: 2,
            apr: 3,
            may: 4,
            jun: 5,
            jul: 6,
            aug: 7,
            sep: 8,
            oct: 9,
            nov: 10,
            dec: 11
        };


        const chartStartDate = this.state.chartData[0].column.date;
        const chartEndDate = this.state.chartData[this.state.chartData.length - 1].column.date;

        this.state.recessionPeriods.forEach( ( recession, i ) => {

            let start, end = 0;

            // Convert the start and end months to integers
            // lowercasing and converting to three letters to standardize months coming from CMS
            const recessionStartMonth = months[recession.start.month.substring( 0, 3 ).toLowerCase()];
            const displayStartMonth = chartStartDate.getMonth();

            const recessionEndMonth = months[recession.end.month.substring( 0, 3 ).toLowerCase()];
            const displayEndMonth = chartEndDate.getMonth();

            const recessionStartDate = new Date( recession.start.year, recessionStartMonth, 1 );
            const recessionEndDate = new Date( recession.end.year, recessionEndMonth, 0 );


            if ( ( recessionStartDate <= chartEndDate ) || ( recessionEndDate >= chartStartDate ) ) {

                // if ( recessionEndDate > chartEndDate ) return;
                // if ( recessionStartDate < chartStartDate ) return;

                if ( recession.start.year > chartStartDate.getFullYear() ) {

                    start = new Date( recession.start.year, recessionStartMonth, 1 );

                    if ( recession.end.year < chartEndDate.getFullYear() ) {

                        end = new Date( recession.end.year, recessionEndMonth, 0 );

                    }
                    else if ( recession.end.year === chartEndDate.getFullYear() ) {

                        if ( recessionEndDate > chartEndDate ) {

                            end = new Date( chartEndDate.getFullYear(), chartEndDate.getMonth(), chartEndDate.getDate() );

                        }
                        else {

                            end = new Date( recession.end.year, recessionEndMonth, 0 );

                        }

                    }
                    else {

                        if ( recessionStartDate > chartEndDate ) return;

                        if ( recessionEndDate > chartEndDate ) {

                            end = new Date( chartEndDate.getFullYear(), chartEndDate.getMonth(), chartEndDate.getDate() );

                        }
                        else {

                            end = new Date( chartEndDate.getFullYear(), displayEndMonth, 0 );

                        }


                    }

                }
                else if ( recession.start.year === chartStartDate.getFullYear() ) {

                    if ( recessionEndDate < chartStartDate ) return;

                    if ( recessionStartMonth > displayStartMonth ) {

                        start = new Date( recession.start.year, recessionStartMonth, 1 );

                    }
                    else {

                        start = new Date( recession.start.year, displayStartMonth, chartStartDate.getDate() );

                    }

                    if ( recession.end.year < chartEndDate.getFullYear() ) {

                        end = new Date( recession.end.year, recessionEndMonth, 0 );

                    }
                    else if ( recession.end.year === chartEndDate.getFullYear() ) {

                        if ( recessionEndMonth >= displayEndMonth ) {

                            end = new Date( chartEndDate.getFullYear(), displayEndMonth, 0 );

                        }
                        else {

                            end = new Date( recession.end.year, recessionEndMonth, 0 );

                        }

                    }
                    else {

                        end = new Date( chartEndDate.getFullYear(), displayEndMonth, 0 );

                        if ( recession.start.year === chartEndDate.getFullYear() && recessionStartMonth > displayEndMonth ) {

                            end = 0;

                        }

                    }


                }
                else if ( recession.start.year < chartStartDate.getFullYear() ) {

                    if ( chartStartDate < recessionEndDate ) {

                        start = new Date( chartStartDate.getFullYear(), chartStartDate.getMonth(), chartStartDate.getDate() );

                        end = recessionEndDate;

                    }
                    else {

                        return;

                    }

                }
                else if ( recession.end.year > chartStartDate.getFullYear() ) {

                    if ( recession.end.year < chartEndDate.getFullYear() ) {

                        start = new Date( chartStartDate.getFullYear(), displayStartMonth, 1 );
                        end = new Date( recession.end.year, recessionEndMonth, 0 );

                    }
                    else if ( recession.end.year === chartEndDate.getFullYear() ) {

                        start = new Date( chartStartDate.getFullYear(), displayStartMonth, 1 );

                        if ( recessionEndMonth < displayEndMonth ) {

                            end = new Date( recession.end.year, recessionEndMonth, 0 );

                        }
                        else if ( recessionEndMonth >= displayEndMonth ) {

                            end = new Date( chartEndDate.getFullYear(), displayEndMonth, 0 );

                        }

                    }
                    else if ( recession.end.year > chartEndDate.getFullYear() ) {

                        start = new Date( chartStartDate.getFullYear(), displayStartMonth, 1 );
                        end = new Date( chartEndDate.getFullYear(), displayEndMonth, 0 );

                    }

                }
                else if ( recession.end.year === chartStartDate.getFullYear() ) {

                    if ( recession.end.year < chartEndDate.getFullYear() ) {

                        start = new Date( chartStartDate.getFullYear(), displayStartMonth, 1 );
                        end = new Date( recession.end.year, recessionEndMonth, 0 );

                    }
                    else if ( recession.end.year === chartEndDate.getFullYear() ) {

                        start = new Date( chartStartDate.getFullYear(), displayStartMonth, 1 );

                        if ( recessionEndMonth < displayEndMonth ) {

                            end = new Date( recession.end.year, recessionEndMonth, 0 );

                        }
                        else if ( recessionEndMonth >= displayEndMonth ) {

                            end = new Date( chartEndDate.getFullYear(), displayEndMonth, 0 );

                        }

                    }
                    else if ( recession.end.year > chartEndDate.getFullYear() ) {

                        start = new Date( chartStartDate.getFullYear(), displayStartMonth, 1 );
                        end = new Date( chartEndDate.getFullYear(), displayEndMonth, 0 );

                    }

                }

                // only add recession areas to chart if the end and start are valid
                if ( ( start instanceof Date && !isNaN( start.getTime() ) ) && ( end instanceof Date && !isNaN( end.getTime() ) ) ) {

                    const height = this.props.height - this.props.margin.top - this.props.margin.bottom;
                    const recessionArea = [
                        {
                            date: start,
                            dStart: this.state.domain.minValue,
                            dEnd: height
                        },
                        {
                            date: end,
                            dStart: this.state.domain.minValue,
                            dEnd: height
                        }
                    ];

                    const recessionFunc = area()
                        .x( ( d ) => this.xScale( d.date ) )
                        .y0( ( d ) => {

                            return this.yScale.range()[1];

                        } )
                        .y1( ( d ) => {

                            return this.yScale.range()[0];

                        } );

                    areaArr.push(
                        <path
                            d={ recessionFunc( recessionArea ) }
                            fill='#EEEEEE'
                            key={ `recession-${ i }` }
                            className='area'
                        />
                    );

                }

            }

        } );

        return areaArr;

    };

    /**
     * Renders the onHover tooltip
     * @returns {HTMLDivElement} - Returns <div> containing the tooltip
     */
    renderToolTip = () => {

        // if only plotting one line
        if ( this.state.tooltipData.rows.length === 1 ) {

            const month = this.state.tooltipData.column.month;
            const day = this.state.tooltipData.column.date.getDate();
            const year = this.state.tooltipData.column.date.getFullYear();


            return (
                <div className='tooltip' ref={ this.toolTipRef }>
                    <h2>{ `${ month } ${ day }, ${ year } ` }</h2>
                    <table>
                        <tbody>
                            <tr >
                                <td className='color' style={{ backgroundColor: this.state.tooltipData.rows[0].color }}></td>
                                <td className='value one-line'>{ this.state.tooltipData.rows[0].value.toFixed( this.toFixedValue ) }</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            );

        }

        const tooltipItems = this.state.tooltipData.rows.map( ( line, i ) => {

            if ( line.visible ) {

                return (
                    <tr key={ `tooltip-${ line.name }-${ i }` } >
                        <td className='color' style={{ backgroundColor: line.color }}></td>
                        <td className='name'>{ line.name }</td>
                        <td className='value'>{ `${ line.value === null ? 'N/A' : `${ line.value.toFixed( this.toFixedValue ) }` }` }</td>
                    </tr>
                );

            }

        } );

        return (
            <div className='tooltip' ref={ this.toolTipRef }>
                <h4>{ `${ this.state.tooltipData.column.month } ${ this.state.tooltipData.column.date.getDate() }, ${ this.state.tooltipData.column.date.getFullYear() } ` }</h4>
                <table>
                    <tbody>
                        { tooltipItems }
                    </tbody>
                </table>
            </div>
        );

    };

    /**
     * Function to update the Tooltip values when the scrubber line moves over svg
     *
     * @param {Number} [month] - Current month being hovered over
     * @param {Number} [day] - Current day being hovered over
     * @param {Number} [year] - Current year being hovered over
     *
     */
    updateToolTip = ( month, day, year ) => {

        let currData;

        this.state.chartData.forEach( ( obj ) => {

            if ( obj.column.date.getFullYear() === year
                && obj.column.date.getMonth() === month
                && obj.column.date.getDate() === day ) {

                currData = obj;

            }

        } );
        // if specifc year/month pair doesn't exist in data don't update tooltip
        if ( currData ) {

            this.setState( { tooltipData: currData } );

        }

    };

    /**
     * Function to handle and transform scrubber line, dots, and tooltip
     * on hover over the chart area
     * @param {Event} [e] - Event object
     *
     */
    handleSliderHover = ( e ) => {

        this.scrubberRef.current.style.opacity = 1;
        const lines = this.lineRef.current.childNodes;

        const yStartVal = 0;
        const yEndVal = this.props.height - this.props.margin.bottom - this.props.margin.top;

        const pageMarginLeft = this.props.pageWrapper.offsetLeft;
        const chartLineMarginLeft = this.props.componentWrapper.offsetLeft;
        const mouseX = e.pageX;

        const pageMarginTop = this.props.pageWrapper.offsetTop;
        const chartLineMarginTop = this.props.componentWrapper.offsetTop;

        const yOffset = e.pageY - pageMarginTop - chartLineMarginTop;
        const yVal = Math.abs( yOffset - this.props.margin.top );

        const xOffset = mouseX - pageMarginLeft - chartLineMarginLeft;
        const xVal = Math.abs( xOffset - this.props.margin.left );

        const date = this.xScale.invert( xVal );
        const bisectDate = bisector( function ( d ) {

            return d.column.date;

        } ).left;
        const i = bisectDate( this.state.chartData, date, 1 ),
            d0 = this.state.chartData[i - 1],
            d1 = this.state.chartData[i],
            selectedPoint = date - d0.column.date > d1.column.date - date ? d1 : d0;

        const dataMonth = selectedPoint.column.date.getMonth();
        const dataDay = selectedPoint.column.date.getDate();
        const dataYear = selectedPoint.column.date.getFullYear();

        this.updateToolTip( dataMonth, dataDay, dataYear );

        // Move dots along lines
        const x = this.xScale( selectedPoint.column.date );

        // Move dots along lines
        selectAll( '.dot-per-line' )
            .attr( 'transform', ( ( d, i ) => {

                if ( selectedPoint.rows[i].value === null ) return 'none';

                const y = this.yScale( selectedPoint.rows[i].value );

                return `translate(${ x },${ y })`;

            } ) )
            .attr( 'fill', '#ffffff' )
            .attr( 'stroke', ( d, i ) => lines[i].getAttribute( 'stroke' ) )
            .attr( 'opacity', ( ( d, i ) => {

                return selectedPoint.rows[i].value === null ? 0 : 1;

            } ) );

        this.toolTipRef.current.style.display = 'block';
        this.toolTipRef.current.style.top = `${ yVal + 30 }px`;

        this.setState( { mouseX }, () => {

            const startTooltip = xVal + this.props.margin.left;
            const tooltipWidth = this.toolTipRef.current.getBoundingClientRect().width;
            const tooltipOffset = 16;
            const centerChart = ( this.props.width - this.props.margin.right - this.props.margin.left ) / 2;
            const mobileMiddleAlignZone = 50;

            // moved to resize observer
            this.props.width < 768 ?
                this.toolTipRef.current.style.left = xVal <= centerChart - mobileMiddleAlignZone
                    ? `${ startTooltip + tooltipOffset }px`
                    : this.toolTipRef.current.style.left = xVal <= centerChart + mobileMiddleAlignZone
                        ? `${ startTooltip - ( tooltipWidth / 2 ) }px`
                        : `${ startTooltip - tooltipWidth - tooltipOffset }px`
                :
                this.toolTipRef.current.style.left = xVal <= centerChart
                    ? `${ startTooltip + tooltipOffset }px`
                    : `${ startTooltip - tooltipWidth - tooltipOffset }px`;

            // Move scrubber line
            select( '.scrubber' )
                .attr( 'd', () => {

                    let d = `M${ xVal },${ yStartVal }`;

                    d += ` ${ xVal },${ yEndVal }`;

                    return d;

                } );

        } );

    };

    getDateWidth( selectedPoint ) {

        const fontFace = 'Graphik-Semibold';
        const fontSize = 16;

        const dataMonth = selectedPoint.column.month;
        const dataDay = selectedPoint.column.date.getDate();
        const dataYear = selectedPoint.column.date.getFullYear();

        return getTextWidth( `${ dataMonth } ${ dataDay }, ${ dataYear } `, fontSize, fontFace ) + 24;

    }

    getMultipleToolTipPosition( dataPoints ) {

        const fontFaceName = 'Graphik-Regular';
        const fontFace = 'Graphik-Semibold';
        const fontSize = 12;
        const initalValue = 0;
        const tooltipContainerPadding = 24;
        const tooltipContainerBorderWidth = 4;
        const tableBorderSpacing = 6 * 2;
        const tableTdSquare = 10;
        const tableTdNamePaddingleft = 1 * 2;
        const tableTdValuePaddingLeft = 5 * 2;

        const extraSpace = tooltipContainerPadding + tooltipContainerBorderWidth;


        const getMaxText = ( total, currentValue, currentIndex, arr ) => {

            const d = this.getNameWidth( currentValue.name, fontSize, fontFaceName ) + tableBorderSpacing + tableTdSquare + tableTdNamePaddingleft + tableTdValuePaddingLeft;
            const e = getTextWidth( currentValue.value.toFixed( 5 ), fontSize, fontFace );
            const textWidth = d + e + extraSpace;

            return textWidth > total ? textWidth : total;

        };

        return dataPoints.rows.reduce( getMaxText, initalValue );

    }

    getNameWidth( text, fontSize, fontFace ) {

        return getTextWidth( text, fontSize, fontFace );

    }

    /**
     * Function to handle when scrubber line leaves svg canvas
     * Hides scrubber line, line dots, and tooltip upon exit
     */
    handleSliderOut = () => {

        this.scrubberRef.current.style.opacity = 0;
        this.toolTipRef.current.style.display = 'none';

        const mouseDots = Array.from( document.getElementsByClassName( 'dot-per-line' ) );

        mouseDots.forEach( ( hoverDot ) => {

            hoverDot.setAttribute( 'opacity', 0 );

        } );

    };

    /**
     * Round a number to the nearest integer.
     *
     * @param {Number} num - The number to round.
     *
     * @returns {Number} - The rounded number.
     *
     * @link https://stackoverflow.com/a/41586903
     */
    round = ( num ) => {

        return ( num >= 0 || -1 ) * Math.ceil( Math.abs( num ) );

    };

    /**
     * Function to make xScale function
     *
     * @param {Number} [width] - width of svg
     *
     * @returns {Function}      - xScale function
     *
     * @link https://github.com/d3/d3/blob/master/API.md#scales-d3-scale
     */
    makeXScale = ( width ) => {

        return (
            scaleTime()
                .domain( [ this.state.chartDisplayYears.start.date, this.state.chartDisplayYears.end.date ] )
                .range( [ 0, width ] )
        );

    };

    /**
     * Function to make yScale function
     *
     * @param {Number} [height] - height of svg
     *
     * @returns {Function}      - yScale function
     *
     * https://github.com/d3/d3/blob/master/API.md#scales-d3-scale
     */
    makeYScale = ( height ) => {

        return (
            scaleLinear()
                .domain( [
                    this.round( this.state.domain.minValue ),
                    this.round( this.state.domain.maxValue )
                ] )
                .range( [ height, 0 ] )
                .nice()
        );

    };

    /**
     * Function to make xAxis that displays tick marks every year interval
     *
     * @returns {Function}     - xAxis function
     *
     * https://github.com/d3/d3/blob/master/API.md#axes-d3-axis
     */
    makeYearXAxis = () => {

        return (
            axisBottom( this.xScale )
                .tickSizeInner( 6 )
                .ticks( this.props.width < 768 ? timeYear.every( 4 ) : timeYear.every( 1 ) )
        );

    };

    /**
     * Function to make xAxis that displays tick marks every month interval
     *
     * @returns {Function}     - xAxis function
     *
     * https://github.com/d3/d3/blob/master/API.md#axes-d3-axis
     */
    makeMonthXAxis = () => {

        return (
            axisBottom( this.xScale )
                .tickFormat( ( date ) => {

                    if ( this.state.chartData.length < 37 && date.getMonth() % 3 === 0 ) {

                        return timeFormat( '%b' )( date );

                    }

                    if ( this.state.chartData.length < 13 ) {

                        return timeFormat( '%b' )( date );

                    }

                    return '';

                } )
                .tickSizeInner( 11 )
                .ticks( timeMonth.every( 1 ) ) // each month tick is in month intervals
        );

    };

    /**
     * Function to make yAxis
     *
     * @param {Number} [width] - Width of svg
     *
     * @returns {Function}     - yAxis function
     *
     * https://github.com/d3/d3/blob/master/API.md#axes-d3-axis
     */
    makeYAxis = ( width ) => {

        return (
            axisRight( this.yScale )
                .tickSize( width )
                .tickFormat( ( d ) => ( d ) )
                .ticks( 6 )
        );

    };

    /**
     * Creates the info overlay wrapper div element that is then appended to the body of the document
     * @returns {HTMLElement} - <div> element appended to the body of the document
     */
    createOverlayWrapper = () => {

        const overlay = document.createElement( 'div' );

        overlay.className = 'info-overlay';

        return document.body.insertAdjacentElement( 'beforeend', overlay );

    };

    /**
     * Renders the chart overlay component
     */
    renderOverlay = () => {

        const overlayWrapper = this.createOverlayWrapper();
        const refocus = document.querySelector( '.info-button' );

        const overlayMarkup = (
            <Fragment>
                <div
                    className='info-overlay-header'
                    id='overlay-header'
                    tabIndex='0'
                    dangerouslySetInnerHTML={{ __html: this.props.overlay.heading }}
                ></div>
                <div
                    className='info-overlay-body'
                    id='overlay-body'
                    dangerouslySetInnerHTML={{ __html: this.props.overlay.body }}
                ></div>
            </Fragment>
        );

        render(
            <OverlayModal
                isOpen={ true }
                wrapper={ overlayWrapper }
                refocusElement={ refocus }
                overlayMarkup={ overlayMarkup }
            />,
            overlayWrapper
        );

    };

    handleLineThicknessChange = ( value ) => {

        this.setState( ( state, props ) => {

            return {
                lineThicknessValue: value
            };

        } );

    };

    handleDataRangeChange = ( dateValues ) => {

        if ( dateValues === null ) return;

        const startValue = this.sanitizedChartData.filter( ( x ) => x.column.date.getTime() === dateValues[0].getTime() ),
            endValue = this.sanitizedChartData.filter( ( x ) => x.column.date.getTime() === dateValues[1].getTime() );

        // update state of newly selected year
        this.setState( ( state, props ) => {

            const selectedChartData = this.sanitizedChartData.slice( startValue[0].column.index, endValue[0].column.index + 1 );

            const selectedChartDisplayYears = {
                start: selectedChartData[0].column,
                end: selectedChartData[selectedChartData.length - 1].column
            };

            this.getDomain( selectedChartData );

            return {
                domain: this.getDomain( selectedChartData ),
                chartData: selectedChartData,
                chartDisplayYears: selectedChartDisplayYears,
                sliderRange: dateValues
            };

        } );

    };

    render() {

        const { margin } = this.props;

        // see https://bl.ocks.org/mbostock/3019563 for explanation of margin convention with d3
        // outerWidth and outerHeight describe the svg width and height
        const outerWidth = this.props.width;
        const outerHeight = this.props.height;

        // width and height are the inner dimensions of the chart and accomodate space for axis labels
        const width = outerWidth - margin.left - margin.right;
        const height = outerHeight - margin.top - margin.bottom;
        const translate = `translate(${ margin.left },${ margin.top })`;

        // 2 years, the monthly axis is shown
        const showMonthlyAxis = this.state.chartData.length < 730;

        this.xScale = this.makeXScale( width );
        this.yScale = this.makeYScale( height );

        const yearXAxis = this.makeYearXAxis();
        const monthXAxis = this.makeMonthXAxis();
        const yAxis = this.makeYAxis( width );

        const textXPosition = 23;

        return (

            <div className='chart-line-wrapper'>
                <LineThickness updateThickness={ this.handleLineThicknessChange } defaultThicknessValue={ 2 } />
                <div className='svg-wrapper' ref={ this.svgRef }>
                    <svg className='chart-line-svg' ref={ this.props.svgRef } width={ outerWidth } height={ outerHeight }>
                        <rect data-desc='background color for IE/Edge' fill='#fff' width={ outerWidth } height={ outerHeight } />
                        <text ref={ this.headerRef } x='8' y='35' className='svg-header' fontSize='24px' fontFamily='TiemposHeadline-Medium' fill='#011e4d'>{ this.props.chart.heading }</text>
                        { /* <text x='8' y='40' className='svg-sub-header' fontSize='16px' fontFamily='Graphik-Regular' fill='#515c70'>{ this.props.chart.subHeading }</text> */ }
                        <text ref={ this.subHeaderRef }x='8' y={ `${ this.props.chart.subHeading.length > 0 ? '73' : '60' }` } className='svg-sub-header' fontSize='16px' fontFamily='Graphik-Regular' fill='#515c70'>{ this.props.chart.lastUpdated }</text>
                        <text x={ textXPosition } y='98' className='y-axis-title' fontSize='16px' fontFamily='Graphik-Bold' fill='#011e4d' >
                            <tspan x={ textXPosition } >{ this.props.chart.yAxisTitle.toUpperCase() }</tspan>
                            <tspan x={ textXPosition } dy='16'>{ this.props.chart.yAxisTitle2.toUpperCase() }</tspan>
                        </text>
                        <g transform={ translate }>
                            <g className='recession-periods'> { this.renderRecessionPeriod() } </g>
                            <Axis axis={ yAxis } height={ height } customAxis={ this.customYAxis } axisType='y' />
                            <Axis axis={ yearXAxis } height={ height } customAxis={ this.customXYearAxis } axisType='x' />
                            { showMonthlyAxis &&
                                <Axis axis={ monthXAxis } height={ height } axisType='x' />
                            }
                            <g className='lines' ref={ this.lineRef } >
                                { this.renderLines() }
                            </g>
                            <g className='dots-scrubber'>
                                <path ref={ this.scrubberRef } className='scrubber'></path>
                                { this.renderHoverDots() }
                                <rect
                                    className='mouse-over-canvas'
                                    height={ height }
                                    width={ width }
                                    fill='none'
                                    onMouseMove={ this.handleSliderHover }
                                    onFocus={ this.handleSliderHover }
                                    onMouseOut={ this.handleSliderOut }
                                    onBlur={ this.handleSliderOut }
                                />
                            </g>
                        </g>
                        { /* <text transform={ `translate(11,${ this.props.height - 36 })` } className='disclaimer' fill='#515c70' fontFamily='Graphik-RegularItalic' fontSize='12px' >{ this.props.disclaimer.sources }</text> */ }
                        <text transform={ `translate(11,${ this.props.height - 62 })` } className='disclaimer' fill='#515c70' fontFamily='Graphik-RegularItalic' fontSize='12px' >{ this.props.disclaimer.notes }</text>
                        <text transform={ `translate(11,${ this.props.height - 42 })` } className='disclaimer' fill='#515c70' fontFamily='Graphik-RegularItalic' fontSize='12px' >{ this.props.disclaimer.sources }</text>                     </svg>
                    { /* <svg className='chart-focus-svg' ref={ this.focusRef } width={ outerWidth } height={ 200 }>
                        <Axis axis={ yearXAxis } height={ height } customAxis={ this.customXYearAxis } axisType='x' />
                        <Axis axis={ monthXAxis } height={ height } axisType='x' />
                    </svg> */ }
                    { this.renderToolTip() }
                </div>
                <div className='range-wrapper' style={{ top: `${ this.props.height - 100 }px` }}>
                    <RangeSlider
                        updateRangeSelection={ this.handleDataRangeChange }
                        chartData={ this.props.data }
                        margin={ this.props.margin }
                        chartWidth={ outerWidth }
                        height={ 40 }
                        makeXScale={ this.makeXScale }
                        makeYScale={ this.makeYScale }
                    />
                    <div className='chart-details'>
                        { /* <button ref={ this.modalRef } onClick={ this.renderOverlay } className='info-button icon-info-utility'><span className='info-button-text'>More about the data series</span></button> */ }
                        { this.props.disclaimer.notes ? <p>{ this.props.disclaimer.notes }</p> : '' }
                        { this.props.disclaimer.sources ? <p>{ this.props.disclaimer.sources }</p> : '' }
                    </div>
                </div>
            </div>
        );

    }

}